import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { format, subDays, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, parseISO } from "date-fns";
import { GetEnquiry } from "../../api/enquiryService";
import FullScreenLoader from "../../components/Common/Loader";

export default function EnquiryPage() {
    document.title = "Enquiry";

    const navigate = useNavigate();
    const [openPicker, setOpenPicker] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [selectedRange, setSelectedRange] = useState({ start: null, end: null });
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const pickerRef = useRef(null);
    const inputRef = useRef(null);
    const [showLoader, setShowLoader] = useState(false);
    const today = new Date();
    const yesterday = subDays(today, 1);
    const lastWeekStart = startOfWeek(subDays(today, 7));
    const lastWeekEnd = endOfWeek(subDays(today, 7));
    const lastMonthStart = startOfMonth(subDays(today, 30));
    const lastMonthEnd = endOfMonth(subDays(today, 30));
    const lastQuarterStart = startOfQuarter(subDays(today, 90));
    const lastQuarterEnd = endOfQuarter(subDays(today, 90));
    const [errorMessage, setErrorMessage] = useState(""); 
    const [enquiries, setEnquiries] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [dataReady, setDataReady] = useState(false);

    const quickRanges = [
        {
            label: "Today",
            getRange: () => ({ start: today, end: today })
        },
        {
            label: "Yesterday",
            getRange: () => ({ start: yesterday, end: yesterday })
        },
        {
            label: "Last week",
            getRange: () => ({ start: lastWeekStart, end: lastWeekEnd })
        },
        {
            label: "Last month",
            getRange: () => ({ start: lastMonthStart, end: lastMonthEnd })
        },
        {
            label: "Last quarter",
            getRange: () => ({ start: lastQuarterStart, end: lastQuarterEnd })
        },
    ];

    const handleQuickRangeSelect = (range) => {
        setSelectedRange(range);
        setOpenPicker(false);
    };

    const handleDateSelect = (date) => {
        if (!selectedRange.start || (selectedRange.start && selectedRange.end)) {
            setSelectedRange({ start: date, end: null });
        } else {
            if (date < selectedRange.start) {
                setSelectedRange({ start: date, end: selectedRange.start });
            } else {
                setSelectedRange({ ...selectedRange, end: date });
            }
        }
    };

    const formatDateKeepTime = (dateTime) => {
        if (!dateTime) return "-";

        const date = parseISO(dateTime);
        return format(date, "dd MMM yyyy hh:mm a");
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const days = [];

        const firstDayOfWeek = firstDay.getDay();
        const prevMonthLastDay = new Date(year, month, 0).getDate();
        for (let i = firstDayOfWeek - 1; i >= 0; i--) {
            days.push({
                date: new Date(year, month - 1, prevMonthLastDay - i),
                isCurrentMonth: false
            });
        }

        for (let i = 1; i <= lastDay.getDate(); i++) {
            days.push({
                date: new Date(year, month, i),
                isCurrentMonth: true
            });
        }

        const totalCells = 42;
        while (days.length < totalCells) {
            const nextDate = new Date(year, month + 1, days.length - lastDay.getDate() - firstDayOfWeek + 1);
            days.push({
                date: nextDate,
                isCurrentMonth: false
            });
        }

        return days;
    };

    const isDateInRange = (date) => {
        if (!selectedRange.start) return false;
        if (!selectedRange.end) return date.getTime() === selectedRange.start.getTime();

        const start = selectedRange.start < selectedRange.end ? selectedRange.start : selectedRange.end;
        const end = selectedRange.start < selectedRange.end ? selectedRange.end : selectedRange.start;

        return date >= start && date <= end;
    };

    const isDateStart = (date) => {
        return selectedRange.start && date.getTime() === selectedRange.start.getTime();
    };

    const isDateEnd = (date) => {
        return selectedRange.end && date.getTime() === selectedRange.end.getTime();
    };

    const days = getDaysInMonth(currentMonth);
    const weekDays = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

    const filteredData = enquiries
        .filter((item) => {
            if (!selectedRange.start || !selectedRange.end) return true;

            const itemDate = new Date(item.ENQUIRYDATE);
            const start = startOfDay(selectedRange.start);
            const end = endOfDay(selectedRange.end);
            const status = endOfDay(item.STATUS);


            return itemDate >= start && itemDate <= end;
        })
        .filter((item) => {
            const search = searchText.toLowerCase();

            return (
                item.ENQUIRYNO?.toLowerCase().includes(search) ||
                item.STATUSCOMMENT?.toLowerCase().includes(search) ||
                item.STATUS?.toLowerCase().includes(search) ||
                String(item.STYLECOUNT ?? "").includes(search)
            );
        });

    const fetchGetEnquiryDetail = async () => {
        setShowLoader(true);
        setDataReady(false);      
        try {
            const payload = {
                EnquirySearch: "",
                FromDate: "",
                ToDate: "",
                PageNo: "1",
                PageSize: "50",
            };

            const response = await GetEnquiry(payload);
            if (response?.Message?.[0]?.RETURNCODE !== "1") { 
                setErrorMessage(response?.Message?.[0]?.RETURNMESSAGE || "Something went wrong.");
                return;
            }

            setEnquiries(response?.Enquiry || []);
            setPagination(response?.Pagination?.[0] || null);           
        } catch (error) {
            console.error("GetEnquiry API Error", error);
        } finally {
            setProgress(100);
            setTimeout(() => {
                setShowLoader(false);
                setDataReady(true);
            }, 400);
        }
    };

    useEffect(() => {
        fetchGetEnquiryDetail();
    }, []);

    useEffect(() => {
        if (!showLoader) return;
        let value = 10;
        setProgress(value);
        const interval = setInterval(() => {
            value = Math.min(value + Math.random() * 15, 90);
            setProgress(value);
        }, 400);
        return () => clearInterval(interval);
    }, [showLoader]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (
                pickerRef.current &&
                !pickerRef.current.contains(event.target) &&
                inputRef.current &&
                !inputRef.current.contains(event.target)
            ) {
                setOpenPicker(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="container py-3">
            {showLoader && <FullScreenLoader progress={progress} />}
            <div className="row g-3 align-items-center col-12">
                <div className="enquiry-header-text col-lg-5">
                    <h1 className="fw-bold text-primary">My Enquiries</h1>
                    <p>({pagination?.Total_Record || filteredData.length})</p>
                </div>
                <div className="col-lg-5 col-md-6">
                    <input
                        ref={inputRef}
                        readOnly
                        className="form-control"
                        style={{ cursor: "pointer", height: "45px" }}
                        value={
                            selectedRange.start && selectedRange.end
                                ? `${format(selectedRange.start, "dd MMM yy")} - ${format(selectedRange.end, "dd MMM yy")}`
                                : "Select Date Range"
                        }
                        onClick={() => setOpenPicker(!openPicker)}
                    />

                    {openPicker && (
                        <div ref={pickerRef} className="date-range-picker">
                            <div className="d-flex flex-column flex-md-row">
                                <div className="me-md-3 mb-3 mb-md-0 quick-range-container">
                                    {quickRanges.map((item, i) => (
                                        <button
                                            key={i}
                                            className="btn btn-outline-primary w-100 mb-2 quick-range-btn"
                                            onClick={() => handleQuickRangeSelect(item.getRange())}
                                        >
                                            {item.label}
                                        </button>
                                    ))}

                                    <button
                                        className="btn btn-outline-primary w-100 mt-2 quick-range-btn"
                                        onClick={() => { setSelectedRange({ start: null, end: null }); setOpenPicker(false);}}
                                    >
                                        Reset
                                    </button>
                                </div>


                                <div style={{ flex: 1 }}>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <button className="btn btn-sm btn-outline-primary" onClick={() => setCurrentMonth(subDays(currentMonth, 30))}>
                                            <i className="bx bx-chevron-left"></i>
                                        </button>

                                        <div className="fw-bold">
                                            {format(currentMonth, "MMMM yyyy")}
                                        </div>

                                        <button className="btn btn-sm btn-outline-primary" onClick={() => setCurrentMonth(subDays(currentMonth, -30))} >
                                            <i className="bx bx-chevron-right"></i>
                                        </button>
                                    </div>
                                    <div className="d-flex week-days">
                                        {weekDays.map((day, i) => (
                                            <div key={i} className="week-day"> {day} </div>
                                        ))}
                                    </div>

                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px" }}>
                                        {days.map((day, i) => {
                                            const isInRange = isDateInRange(day.date);
                                            const isStart = isDateStart(day.date);
                                            const isEnd = isDateEnd(day.date);
                                            const isToday = format(day.date, "yyyy-MM-dd") === format(today, "yyyy-MM-dd");

                                            return (
                                                <button
                                                    key={i}
                                                    className="btn btn-sm"
                                                    onClick={() => handleDateSelect(day.date)}
                                                    style={{
                                                        height: "32px",
                                                        padding: 0,
                                                        fontSize: "12px",
                                                        backgroundColor: isStart || isEnd ? "#007bff" : isInRange ? "#e7f1ff" : "transparent",
                                                        color: isStart || isEnd ? "#fff" : !day.isCurrentMonth ? "#ccc" : isToday ? "#007bff" : "#000",
                                                        borderRadius: isStart ? "50% 0 0 50%" : isEnd ? "0 50% 50% 0" : isInRange ? "0" : "50%",
                                                        border: isToday && !isStart && !isEnd && !isInRange ? "1px solid #007bff" : "1px solid transparent"
                                                    }}
                                                >
                                                    {day.date.getDate()}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="col-lg-2 col-md-6">
                    <input
                        type="text"
                        placeholder="Search Enquiry..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className="form-control rounded-pill"
                        style={{ height: "45px", paddingLeft: "20px" }}
                    />
                    {searchText && (
                        <span
                            onClick={() => setSearchText("")}
                            style={{
                                position: "absolute",
                                right: "25px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                cursor: "pointer",
                                fontSize: "18px",
                                color: "#888",
                            }}
                        >
                            &times;
                        </span>
                    )}
                </div>
            </div>

            <div className="row mt-1 g-3">
                {!loading && errorMessage ? (
                    <div className="enquiry-empty-wrapper">
                        <div className="enquiry-empty-state text-center">
                            <img
                                src="/assets/images/enquiry/noEnquiry.png"
                                alt="Error"
                                className="enquiry-empty-image mb-3"
                                style={{ maxWidth: "150px" }}
                            />
                            <h5 className="fw-semibold mb-2">Error</h5>
                            <p className="text-muted mb-3">{errorMessage}</p>
                        </div>
                    </div>
                ) : null}
                {!loading && !errorMessage && filteredData.length === 0 && (
                    <div className="enquiry-empty-wrapper">
                        <div className="enquiry-empty-state text-center">
                            <img
                                src="/assets/images/enquiry/noEnquiry.png"
                                alt="No Enquiry"
                                className="enquiry-empty-image mb-3"
                                style={{ maxWidth: "150px" }}
                            />
                            <h5 className="fw-semibold mb-2">No Enquiry Available</h5>
                            <p className="text-muted mb-3">
                                Looks like you haven't added any enquiry yet.
                            </p>
                        </div>
                    </div>
                )}
                {filteredData.map((item) => (
                    <div key={item.ENQUIRY_ID} className="col-12 col-sm-4 col-md-4 col-lg-3">
                        <div className="enquiry-card card p-3 shadow-sm rounded-3">

                            <div className="d-flex justify-content-between">
                                <div className="fw-bold">{item.ENQUIRYNO}</div>
                                <span className={`status-badge status-${item.STATUS.toLowerCase()}`}>
                                    {item.STATUS}
                                </span>
                            </div>

                            <div className="text-muted small">
                                {formatDateKeepTime(item.ENTRYDATE)}
                            </div>

                            <hr />

                            <div>
                                <div><strong>T.Style.:</strong> {item.STYLECOUNT ?? "-"}</div>
                                <div className="text-truncate"><strong>Remark:</strong> {item.STATUSCOMMENT ?? "-"}</div>
                            </div>

                            <hr />

                            <button
                                className="primary-outline-btn commomPopup-btn-confirm"
                                style={{ }}
                                onClick={() =>
                                    navigate(`/enquiry-detail/${item.ENQUIRY_ID}`, {
                                        state: {
                                            enquiryNo: item.ENQUIRYNO,
                                            remark: item.STATUSCOMMENT
                                        }
                                    })}
                                >
                                View Enquiry Detail
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}